import type { AdminLocale } from '@/i18n';

/**
 * Stock figures as the inventory screen reads and takes them
 * (docs/07-Admin/Products.md, "Inventory view").
 */

/**
 * Why a recounted figure changed — the API's `ADJUST_REASONS`, in the order an
 * operator meets them. A code list, not data: the API refuses anything else.
 */
export const RECOUNT_REASONS = ['recount', 'supplier_delivery', 'damage', 'loss'] as const;

export type RecountReason = (typeof RECOUNT_REASONS)[number];

/** The thresholds offered as explicit views beside the store's own setting. */
export const LOW_STOCK_THRESHOLDS = [5, 10, 25, 50] as const;

/** The most rows `GET /admin/inventory/low-stock` returns; it has no next page. */
export const LOW_STOCK_LIMIT = 50;

const DIGITS: Record<string, string> = {
  '٠': '0', '١': '1', '٢': '2', '٣': '3', '٤': '4', '٥': '5', '٦': '6', '٧': '7', '٨': '8', '٩': '9',
  '۰': '0', '۱': '1', '۲': '2', '۳': '3', '۴': '4', '۵': '5', '۶': '6', '۷': '7', '۸': '8', '۹': '9',
};

/**
 * A counted figure as typed — a whole number of units, or null.
 *
 * An Arabic keyboard types Arabic-Indic digits; "١٢" on the shelf is twelve,
 * and refusing it would make the operator switch keyboards to say so.
 */
export function parseCount(input: string): number | null {
  const latin = input.trim().replace(/[٠-٩۰-۹]/g, (digit) => DIGITS[digit] ?? digit);
  if (!/^\d+$/.test(latin)) return null;

  const value = Number(latin);
  return Number.isSafeInteger(value) ? value : null;
}

/**
 * A movement's quantity with its sign always written: "+5", "-3", "+٥".
 * Signed because a recount downwards must never read as a delivery.
 */
export function signedCount(quantity: number, locale: AdminLocale): string {
  return new Intl.NumberFormat(locale === 'ar' ? 'ar-SA-u-nu-arab' : 'en-SA', { signDisplay: 'exceptZero' }).format(
    quantity,
  );
}
